import { c as _c } from "react/compiler-runtime";
import { useId, useState, type ChangeEvent, type CSSProperties } from 'react';
import styles from './ColorInput.module.css';
import { TextInput } from '../TextInput';
import { LayoutCluster } from '../layouts';
import TextInputWrapper from '../TextInputWrapper';
import { calculateAlphaHex, calculatePercentageFromHex, getRegularHexPart } from './helpers';
import type { ColorInputProps, SwatchProps } from './types';
import classNames from 'classnames';
import { Icon } from '../Icon';
function Swatch(t0) {
  const $ = _c(20);
  let className;
  let rest;
  let t1;
  let value;
  if ($[0] !== t0) {
    ({
      value,
      alpha: t1,
      className,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = rest;
    $[3] = t1;
    $[4] = value;
  } else {
    className = $[1];
    rest = $[2];
    t1 = $[3];
    value = $[4];
  }
  const alpha = t1 === undefined ? "100" : t1;
  let t2;
  if ($[5] !== alpha) {
    t2 = calculateAlphaHex(alpha);
    $[5] = alpha;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const t3 = `${value}${t2}`;
  let t4;
  if ($[7] !== t3) {
    t4 = {
      "--selected-color-with-alpha": t3
    };
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const t5 = t4 as CSSProperties;
  let t6;
  if ($[9] !== className) {
    t6 = classNames(styles.color, className);
    $[9] = className;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== rest || $[12] !== t6 || $[13] !== value) {
    t7 = <input {...rest} className={t6} type="color" value={value} />;
    $[11] = rest;
    $[12] = t6;
    $[13] = value;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className={styles.transparentBackgroundPattern} />;
    t9 = <div className={styles.selectedColorBackground} />;
    $[15] = t8;
    $[16] = t9;
  } else {
    t8 = $[15];
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t5 || $[18] !== t7) {
    t10 = <div className={styles.colorInputGrid} style={t5}>{t7}{t8}{t9}</div>;
    $[17] = t5;
    $[18] = t7;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  return t10;
}
function normaliseColorValue(color?: string) {
  if (!color) return;
  return color.startsWith('#') ? color : `#${color}`;
}
function ColorInput(props) {
  const $ = _c(40);
  const {
    id: id_,
    value: value_,
    colorInputLabelProps,
    onChange,
    hasTransparency
  } = props;
  const value = normaliseColorValue(value_);
  const [percentage, setPercentage] = useState(value && props.hasTransparency ? calculatePercentageFromHex(value.slice(7)) : "100");
  const fallbackId = useId();
  const id = id_ || props.name || fallbackId;
  if (!hasTransparency) {
    let t0;
    if ($[0] !== onChange || $[1] !== props) {
      t0 = e => {
        onChange?.(e);
        props.onValueChange?.(e.target.value);
      };
      $[0] = onChange;
      $[1] = props;
      $[2] = t0;
    } else {
      t0 = $[2];
    }
    return <TextInputWrapper {...colorInputLabelProps} id={id} name={props.name}><Swatch {...props} id={id} value={value} onChange={t0} /></TextInputWrapper>;
  }
  let onValueChange;
  let swatchProps;
  let transparencyInputProps;
  let transparencyLabelProps;
  if ($[3] !== props) {
    ({
      onValueChange,
      transparencyInputProps,
      transparencyLabelProps,
      ...swatchProps
    } = props);
    $[3] = props;
    $[4] = onValueChange;
    $[5] = swatchProps;
    $[6] = transparencyInputProps;
    $[7] = transparencyLabelProps;
  } else {
    onValueChange = $[4];
    swatchProps = $[5];
    transparencyInputProps = $[6];
    transparencyLabelProps = $[7];
  }
  let t0;
  if ($[8] !== onValueChange || $[9] !== percentage) {
    t0 = function handleColorChange(e_0) {
      onValueChange?.(`${e_0.target.value}${calculateAlphaHex(percentage)}`);
    };
    $[8] = onValueChange;
    $[9] = percentage;
    $[10] = t0;
  } else {
    t0 = $[10];
  }
  const handleColorChange = t0;
  const handleAlphaChange = function handleAlphaChange(e_1) {
    setPercentage(e_1.target.value);
    onValueChange?.(`${getRegularHexPart(value)}${calculateAlphaHex(e_1.target.value)}`);
  };
  const T0 = LayoutCluster;
  const t1 = "300";
  const T1 = TextInputWrapper;
  const T2 = Swatch;
  const t2 = getRegularHexPart(value);
  let t3;
  if ($[11] !== T2 || $[12] !== handleColorChange || $[13] !== id || $[14] !== percentage || $[15] !== swatchProps || $[16] !== t2) {
    t3 = <T2 {...swatchProps} value={t2} alpha={percentage} onChange={handleColorChange} id={id} />;
    $[11] = T2;
    $[12] = handleColorChange;
    $[13] = id;
    $[14] = percentage;
    $[15] = swatchProps;
    $[16] = t2;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  let t4;
  if ($[18] !== T1 || $[19] !== colorInputLabelProps || $[20] !== id || $[21] !== t3) {
    t4 = <T1 {...colorInputLabelProps} id={id}>{t3}</T1>;
    $[18] = T1;
    $[19] = colorInputLabelProps;
    $[20] = id;
    $[21] = t3;
    $[22] = t4;
  } else {
    t4 = $[22];
  }
  const t5 = props.transparencyLabelProps?.className;
  let t6;
  if ($[23] !== t5) {
    t6 = classNames(styles.transparencyWrapper, t5);
    $[23] = t5;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  const t7 = `transparency-${id}`;
  const t8 = `transparency-${id}`;
  let t9;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Icon name="percentage" />;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== handleAlphaChange || $[27] !== percentage || $[28] !== t8 || $[29] !== transparencyInputProps) {
    t10 = <TextInput {...transparencyInputProps} value={percentage} onChange={handleAlphaChange} min={0} max={100} id={t8} type="number">{t9}</TextInput>;
    $[26] = handleAlphaChange;
    $[27] = percentage;
    $[28] = t8;
    $[29] = transparencyInputProps;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== t10 || $[32] !== t6 || $[33] !== t7 || $[34] !== transparencyLabelProps) {
    t11 = <TextInputWrapper {...transparencyLabelProps} className={t6} id={t7}>{t10}</TextInputWrapper>;
    $[31] = t10;
    $[32] = t6;
    $[33] = t7;
    $[34] = transparencyLabelProps;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  let t12;
  if ($[36] !== T0 || $[37] !== t11 || $[38] !== t4) {
    t12 = <T0 space={t1}>{t4}{t11}</T0>;
    $[36] = T0;
    $[37] = t11;
    $[38] = t4;
    $[39] = t12;
  } else {
    t12 = $[39];
  }
  return t12;
}
export default ColorInput;